import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const DEALER_FORM_SRC = 'https://app.bontcycling.com/api/dealer-form';

// The external script scopes its styles under `.bont-dealer-form` with
// `color/font: inherit` and transparent, faintly-bordered fields. We override
// the field treatment here using the site's theme variables so the form reads
// as intentional and adapts to dark mode. The `#dealer-form` id outranks the
// script's `.bont-dealer-form` class selectors, so these win regardless of the
// order the two <style> blocks land in <head>.
const DealerFormStyled = styled.div`
  /* 1rem inline gutter on desktop and mobile. No explicit width: the block
     auto-fills its container minus the margins, so it never overflows (setting
     width:100% *and* a margin would push the box to 100% + 2rem and spill). */
  margin: 2rem 1rem 0;

  /* Fill the container instead of the script's 640px cap. */
  .bont-dealer-form {
    max-width: none;
  }

  @media (max-width: 1023px) {
    /* Stack the paired fields (name, city/state, etc.) on mobile. */
    .bont-dealer-form .bdf-row {
      flex-direction: column;
    }
  }

  .bont-dealer-form input[type='text'],
  .bont-dealer-form input[type='email'],
  .bont-dealer-form input[type='tel'],
  .bont-dealer-form input[type='number'],
  .bont-dealer-form input[type='date'],
  .bont-dealer-form textarea,
  .bont-dealer-form select {
    background: var(--backgroundCard);
    /* Full black border on every side. !important beats the site's global
       \`input\` rule, which sets \`border-bottom: ... !important\`. --textColor is
       black in light mode and stays visible in dark mode. */
    border: 1px solid var(--textColor) !important;
    border-radius: var(--borderRadius);
    /* Never exceed the container (fixes dropdowns/textarea spilling past the
       viewport edge) and go full width on mobile. */
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .bont-dealer-form input:focus,
  .bont-dealer-form textarea:focus,
  .bont-dealer-form select:focus {
    border-color: var(--borderColor);
  }

  /* The site's global \`input\` rule forces display:block, min-height:40px and
     margin-bottom:2rem onto every input, which breaks the checkbox/radio
     alignment inside the form. Reset those back for the small toggles so they
     center against their labels (their flex containers already center). */
  .bont-dealer-form input[type='checkbox'],
  .bont-dealer-form input[type='radio'] {
    display: inline-block;
    flex: none;
    width: 16px;
    height: 16px;
    min-height: 0;
    margin: 0;
  }
`;

// React does not execute <script> tags rendered in JSX, so we append the loader
// imperatively once #dealer-form is mounted. useEffect is browser-only, keeping
// this SSR-safe.
export default function DealerForm() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;

    const script = document.createElement('script');
    script.src = DEALER_FORM_SRC;
    script.async = true;
    document.body.appendChild(script);

    // Clean up on unmount / client-side navigation so the form isn't duplicated.
    return () => {
      script.remove();
      if (container) container.innerHTML = '';
    };
  }, []);

  return <DealerFormStyled id="dealer-form" ref={containerRef} />;
}
